import PropTypes from 'prop-types';
import { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
// @mui
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
// iconify
import { Icon } from '@iconify/react';
import homeFill from '@iconify/icons-eva/home-fill';
import bookOpenFill from '@iconify/icons-eva/book-open-fill';
import personFill from '@iconify/icons-eva/person-fill';
// theme
import ThemeProvider from 'src/theme';

// ----------------------------------------------------------------------

// redux
import { useDispatch, useSelector } from 'src/redux/store';
import { getShopById } from 'src/redux/slices/shops';
import { getCourse, getCourseTheme } from 'src/modules/course-creator/v2/redux/slice';
// hooks
import { ShopContext } from 'src/hooks/use-shop-context';
import { paths } from 'src/routes/paths';
import { useSettingsContext } from 'src/components/settings';
import { SettingsContext } from 'src/components/settings/context/settings-context';

// ----------------------------------------------------------------------

export default function StudentLayout({ children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const params = useParams();

    const { shopid } = params;
    const courseId = location.pathname.match(/\/course\/([^/]+)/)?.[1];

    const { shop: shopSettings, isLoading: shopLoading } = useSelector((state) => state.shops);
    const { currentCourse, shopTheme } = useSelector((state) => state.course);

    const themeSettings = useSettingsContext();

    const [value, setValue] = useState(0);

    useEffect(() => {
        if (shopid) {
            dispatch(getShopById(shopid, 'all'));
            dispatch(getCourseTheme(shopid));
        }
    }, [dispatch, shopid]);

    useEffect(() => {
        if (courseId) {
            dispatch(getCourse(courseId));
        }
    }, [dispatch, courseId]);

    const effectiveSettings = useMemo(() => {
        // Priority 1: Course Settings (if inside a specific course)
        if (courseId && currentCourse && currentCourse.id === courseId) {
            return {
                ...themeSettings,
                themeMode: currentCourse.themeMode ?? themeSettings.themeMode,
                themeColorPresets: currentCourse.themeColorPresets ?? themeSettings.themeColorPresets,
                themeBorderRadius: currentCourse.themeBorderRadius ?? themeSettings.themeBorderRadius,
                themeCardColor: currentCourse.themeCardColor ?? themeSettings.themeCardColor,
                themeBgColor: currentCourse.themeBgColor ?? themeSettings.themeBgColor,
                themeTextColor: currentCourse.themeTextColor ?? themeSettings.themeTextColor,
            };
        }

        // Priority 2: Shop Theme Settings from course mock server (global cabinet appearance)
        if (shopTheme && Object.keys(shopTheme).length > 0) {
            return {
                ...themeSettings,
                ...shopTheme,
                themeMode: shopTheme.themeMode ?? themeSettings.themeMode,
            };
        }

        return themeSettings;
    }, [themeSettings, currentCourse, courseId, shopSettings]);

    useEffect(() => {
        if (location.pathname.includes(paths.student.courses(shopid))) {
            setValue(1);
        } else if (location.pathname.includes(paths.student.saved(shopid))) {
            setValue(2);
        } else if (location.pathname.includes(paths.student.profile(shopid))) {
            setValue(3);
        } else {
            setValue(0);
        }
    }, [location.pathname, shopid]);

    return (
        <SettingsContext.Provider value={effectiveSettings}>
            <ThemeProvider>
                <ShopContext.Provider value={{ shopid, settings: shopSettings, isLoading: shopLoading }}>
                    <Box sx={{
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: effectiveSettings.themeBgColor || 'background.default',
                        color: effectiveSettings.themeTextColor || 'text.primary'
                    }}>
                        <Box component="main" sx={{ flexGrow: 1, pb: 7 }}>
                            {children || <Outlet />}
                        </Box>

                        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000 }} elevation={3}>
                            <BottomNavigation
                                showLabels
                                value={value}
                                onChange={(event, newValue) => {
                                    setValue(newValue);
                                    switch (newValue) {
                                        case 0:
                                            navigate(paths.student.dashboard(shopid));
                                            break;
                                        case 1:
                                            navigate(paths.student.courses(shopid));
                                            break;
                                        case 2:
                                            navigate(paths.student.saved(shopid));
                                            break;
                                        case 3:
                                            navigate(paths.student.profile(shopid));
                                            break;
                                        default:
                                            break;
                                    }
                                }}
                            >
                                <BottomNavigationAction label="Home" icon={<Icon icon={homeFill} width={24} height={24} />} />
                                <BottomNavigationAction label="Courses" icon={<Icon icon={bookOpenFill} width={24} height={24} />} />
                                <BottomNavigationAction label="Saved" icon={<Icon icon="solar:heart-bold" width={24} height={24} />} />
                                <BottomNavigationAction label="Profile" icon={<Icon icon={personFill} width={24} height={24} />} />
                            </BottomNavigation>
                        </Paper>
                    </Box>
                </ShopContext.Provider>
            </ThemeProvider>
        </SettingsContext.Provider>
    );
}

StudentLayout.propTypes = {
    children: PropTypes.node,
};
